import React, { useState } from 'react';

import { z } from 'zod';

import { GenericDialog } from '@/components/NewUI/Components';
import { useSingleRoot } from '@/components/NewUI/SingleRoot';

const editConflictSchema = z.object({
  error: z.literal('edit_conflict'),
  editVersion: z.number().int().nonnegative(),
  by: z.object({ userId: z.number(), name: z.string() }).nullish(),
  at: z.iso.datetime().nullish(),
  inApproval: z.boolean().optional()
});

type ConflictState = {
  conflict: z.infer<typeof editConflictSchema>;
  retry: () => void;
  stage: 'conflict' | 'confirm';
} | null;

const useEditConflict = ({
  recordLabel,
  inApproval = false
}: {
  recordLabel: 'job' | 'job template' | 'approval';
  inApproval?: boolean;
}) => {
  const enabled = useSingleRoot() !== null;
  const [state, setState] = useState<ConflictState>(null);

  const handleConflict = (error: unknown, retry: () => void): boolean => {
    const parsed = editConflictSchema.safeParse(error);
    if (!enabled || !parsed.success) return false;

    setState({ conflict: parsed.data, retry, stage: 'conflict' });
    return true;
  };

  const name = state?.conflict.by?.name ?? 'Someone';
  const time = state?.conflict.at
    ? ` at ${new Date(state.conflict.at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      })}`
    : '';
  const confirming = state?.stage === 'confirm';
  const dialog = enabled ? (
    <GenericDialog
      buttonText="Overwrite their changes"
      callbackLoading={false}
      isDialogOpen={state !== null}
      primaryButtonId="overwrite-edit-conflict"
      secondaryButtonId="cancel-edit-conflict"
      secondaryButtonText="Back to editing"
      setDialogOpen={() => setState(null)}
      url=""
      buttonCallback={() => {
        if (!state) return;
        if (state.stage === 'conflict' && (inApproval || state.conflict.inApproval)) {
          setState({ ...state, stage: 'confirm' });
          return;
        }
        setState(null);
        state.retry();
      }}
      description={
        confirming
          ? 'This approval has already entered the approval process. Overwriting may replace changes that its reviewers have seen.'
          : 'Your unsaved changes are still here. Overwriting replaces their changes with yours.'
      }
      title={
        confirming
          ? 'Overwrite this approval already in review?'
          : `${name} saved this ${recordLabel}${time} while you were editing`
      }
    />
  ) : null;

  return { dialog, enabled, handleConflict };
};

export default useEditConflict;
